import { graphqlQuery } from '@/lib/api/graphql';
import { generateMockDiagnostics } from '@/lib/mock-data/strategy-diagnostics-mock-data';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import type { StageBreakdown, StrategyDiagnostics } from '@/types/strategy-diagnostics';
import { useModeAwareQuery } from './useModeAwareQuery';

// ─── GraphQL queries (split to avoid DbContext concurrency) ─────────────────

const DIAGNOSTICS_QUERY = `
  query StrategyDiagnostics($diagFirst: Int, $diagFilter: StrategyDiagnosticsFilterInput) {
    collectionStrategies {
      strategyDiagnostics(first: $diagFirst, filter: $diagFilter) {
        edges {
          node {
            strategyId
            status
            createdAt
            createdByUserName
            openCases
            successRate
            lastRunAt
            nextScheduledRun
            expectedEmails
            emailsSent14Days
            totalCasesEntered
          }
        }
      }
    }
  }
`;

const STAGE_BREAKDOWN_QUERY = `
  query StrategyStageBreakdown(
    $stageFirst: Int,
    $stageFilter: StrategyStageBreakdownFilterInput,
    $stageSort: [StrategyStageBreakdownSortSortByInput!]
  ) {
    collectionStrategies {
      strategyStageBreakdown(first: $stageFirst, filter: $stageFilter, sort: $stageSort) {
        edges {
          node {
            strategyId
            stageOrder
            label
            actionType
            casesAtStage
            activeAtStage
            closedAtStage
            promisedAtStage
            escalatedAtStage
            onHoldAtStage
            percentageClosedHere
            avgDaysAtStage
          }
        }
      }
    }
  }
`;

// ─── GraphQL response types ─────────────────────────────────────────────────

interface DiagnosticsNode {
  strategyId: number;
  status: string;
  createdAt: string;
  createdByUserName: string | null;
  openCases: number;
  successRate: number | null;
  lastRunAt: string | null;
  nextScheduledRun: string | null;
  expectedEmails: number;
  emailsSent14Days: number;
  totalCasesEntered: number;
}

interface StageNode {
  strategyId: number;
  stageOrder: number;
  label: string | null;
  actionType: string | null;
  casesAtStage: number;
  activeAtStage: number;
  closedAtStage: number;
  promisedAtStage: number;
  escalatedAtStage: number;
  onHoldAtStage: number;
  percentageClosedHere: number;
  avgDaysAtStage: number;
}

interface DiagnosticsGraphQLResponse {
  collectionStrategies: {
    strategyDiagnostics: {
      edges: Array<{ node: DiagnosticsNode }>;
    };
  };
}

interface StageBreakdownGraphQLResponse {
  collectionStrategies: {
    strategyStageBreakdown: {
      edges: Array<{ node: StageNode }>;
    };
  };
}

// ─── Mapper ─────────────────────────────────────────────────────────────────

function mapGraphQLToDiagnostics(
  diagResponse: DiagnosticsGraphQLResponse,
  stageResponse: StageBreakdownGraphQLResponse,
  strategyId: number,
): StrategyDiagnostics | null {
  const diagNode = diagResponse.collectionStrategies.strategyDiagnostics.edges[0]?.node;
  if (!diagNode) return null;

  const stages: StageBreakdown[] = stageResponse.collectionStrategies.strategyStageBreakdown.edges.map((edge) => ({
    stageOrder: edge.node.stageOrder,
    label: edge.node.label ?? '',
    actionType: (edge.node.actionType ?? 'email') as StageBreakdown['actionType'],
    casesAtStage: edge.node.casesAtStage,
    activeAtStage: edge.node.activeAtStage,
    closedAtStage: edge.node.closedAtStage,
    promisedAtStage: edge.node.promisedAtStage,
    escalatedAtStage: edge.node.escalatedAtStage,
    onHoldAtStage: edge.node.onHoldAtStage,
    percentageClosedHere: edge.node.percentageClosedHere,
    avgDaysAtStage: edge.node.avgDaysAtStage,
  }));

  return {
    id: `diag-${strategyId}`,
    strategyId: String(strategyId),
    status: diagNode.status === 'Active' ? 'Active' : 'Inactive',
    createdBy: {
      name: diagNode.createdByUserName ?? 'System',
      id: '',
    },
    createdAt: new Date(diagNode.createdAt),
    lastRun: diagNode.lastRunAt ? new Date(diagNode.lastRunAt) : undefined,
    nextScheduledRun: diagNode.nextScheduledRun ? new Date(diagNode.nextScheduledRun) : undefined,
    activeAccounts: diagNode.openCases,
    expectedEmails: diagNode.expectedEmails,
    emailsSent14Days: diagNode.emailsSent14Days,
    successRate: diagNode.successRate ?? 0,
    impactAnalysis: {},
    stageFunnel: {
      totalCasesEntered: diagNode.totalCasesEntered,
      stages,
    },
    auditTrail: [],
  };
}

// ─── Hook ───────────────────────────────────────────────────────────────────

const mockDiagnosticsMap = generateMockDiagnostics();

/**
 * Fetches strategy diagnostics and stage breakdown from the GraphQL API.
 *
 * In **demo** mode returns mock data from `generateMockDiagnostics()`.
 * In **live** mode executes two sequential GraphQL calls to avoid
 * DbContext concurrency issues (EF Core is not thread-safe).
 */
export function useStrategyDiagnostics(strategyId: string | number | null) {
  const apiConfig = useApiConfig();

  const mockData: StrategyDiagnostics | null = strategyId
    ? (mockDiagnosticsMap[String(strategyId)] ?? mockDiagnosticsMap[`strategy-${strategyId}`] ?? null)
    : null;

  return useModeAwareQuery<StrategyDiagnostics | null, Error>(
    ['strategy-diagnostics', strategyId],
    async ({ signal }) => {
      if (!strategyId) return null;

      const numericId = Number(strategyId);

      // Execute sequentially to avoid DbContext concurrency (EF Core is not thread-safe)
      const diagResponse = await graphqlQuery<DiagnosticsGraphQLResponse>(
        apiConfig,
        DIAGNOSTICS_QUERY,
        {
          diagFirst: 1,
          diagFilter: { strategyId: { eq: numericId } },
        },
        signal,
      );

      const stageResponse = await graphqlQuery<StageBreakdownGraphQLResponse>(
        apiConfig,
        STAGE_BREAKDOWN_QUERY,
        {
          stageFirst: 50,
          stageFilter: { strategyId: { eq: numericId } },
          stageSort: [{ field: 'StageOrder', order: 'Ascending' }],
        },
        signal,
      );

      return mapGraphQLToDiagnostics(diagResponse, stageResponse, numericId);
    },
    mockData,
    {
      enabled: !!strategyId,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  );
}
